import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Stack,
  Typography, TextField, IconButton, alpha, Snackbar, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PollOutlinedIcon from '@mui/icons-material/PollOutlined';
import { CRM_PALETTE as P } from '../../theme/brandPalette';
import {
  useGetActiveSurveyQuery, useGetSurveyForMeQuery, useRespondSurveyMutation,
  type SurveyQuestion, type SurveyAnswer, type Survey,
} from '../../store/api/platformApi';

const golden = P.brand.golden;

type Toast = { msg: string; severity: 'success' | 'info' };

/**
 * Encuesta de satisfacción para el agente:
 *  - Banner cuando hay una encuesta activa sin responder.
 *  - Deep-link `?survey=<id>` (desde la notificación): abre la encuesta directo,
 *    o muestra un alert "ya la respondiste" / "no disponible".
 * Genérico: sirve para cualquier encuesta futura.
 */
export function SurveyPrompt() {
  const [searchParams, setSearchParams] = useSearchParams();
  const surveyParam = searchParams.get('survey');

  const { data: active } = useGetActiveSurveyQuery();
  const { data: deepLink } = useGetSurveyForMeQuery(surveyParam ?? '', { skip: !surveyParam });
  const [respond, { isLoading }] = useRespondSurveyMutation();

  const [dismissed, setDismissed] = useState(false);
  const [responding, setResponding] = useState<Survey | null>(null);   // encuesta abierta en el diálogo
  const [answers, setAnswers] = useState<Record<string, number | string | null>>({});
  const [toast, setToast] = useState<Toast | null>(null);

  const activeSurvey = active?.survey ?? null;

  // ── Deep-link desde la notificación ─────────────────────────────────────────
  useEffect(() => {
    if (!surveyParam || !deepLink) return;
    if (deepLink.answered) {
      setToast({ msg: `Ya respondiste "${deepLink.survey.title}". ¡Gracias por tu feedback! 🙌`, severity: 'success' });
    } else if (deepLink.survey.status === 'active') {
      setAnswers({});
      setResponding(deepLink.survey);
    } else {
      setToast({ msg: `La encuesta "${deepLink.survey.title}" ya no está disponible.`, severity: 'info' });
    }
    // limpiar el param para no re-disparar
    const next = new URLSearchParams(searchParams);
    next.delete('survey');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyParam, deepLink]);

  const setAns = (qid: string, value: number | string | null) =>
    setAnswers((a) => ({ ...a, [qid]: value }));

  const openDialog = (s: Survey) => { setAnswers({}); setResponding(s); };

  const handleSubmit = async () => {
    if (!responding) return;
    const payload: SurveyAnswer[] = responding.questions.map((q) => ({ questionId: q.id, value: answers[q.id] ?? null }));
    try {
      await respond({ id: responding._id, answers: payload }).unwrap();
      setResponding(null);
      setDismissed(true);
      setToast({ msg: '¡Gracias por tu respuesta! 🙌', severity: 'success' });
    } catch { /* el invalidate de SurveyActive lo saca igual */ }
  };

  const flotante = activeSurvey?.display === 'floating';
  const showBanner = activeSurvey && !dismissed && !flotante;

  return (
    <>
      {/* Banner */}
      {showBanner && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
          px: 2, py: 1.25, mb: 2, borderRadius: 2,
          border: `1px solid ${alpha(golden, 0.35)}`, bgcolor: alpha(golden, 0.08),
        }}>
          <PollOutlinedIcon sx={{ color: golden }} />
          <Typography sx={{ flex: 1, minWidth: 200, fontSize: 14 }}>
            <strong>{activeSurvey!.title}</strong> — contanos tu experiencia, te lleva 2 minutos.
          </Typography>
          <Button size="small" variant="contained" onClick={() => openDialog(activeSurvey!)}
            sx={{ fontWeight: 700 }}>
            Responder
          </Button>
          <IconButton size="small" onClick={() => setDismissed(true)} sx={{ color: 'text.disabled' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {flotante && !dismissed && (
        <EncuestaFlotante survey={activeSurvey!} answers={answers} onAnswer={setAns}
          enviando={isLoading} onClose={() => setDismissed(true)}
          onSubmit={async () => {
            const payload: SurveyAnswer[] = activeSurvey!.questions.map((q) => ({ questionId: q.id, value: answers[q.id] ?? null }));
            try {
              await respond({ id: activeSurvey!._id, answers: payload }).unwrap();
              setDismissed(true);
              setToast({ msg: '¡Gracias por tu respuesta! 🙌', severity: 'success' });
            } catch { /* el invalidate de SurveyActive lo saca igual */ }
          }} />
      )}

      {/* Diálogo de respuesta */}
      <Dialog open={!!responding} onClose={() => setResponding(null)} maxWidth="sm" fullWidth>
        {responding && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>{responding.title}</DialogTitle>
            <DialogContent>
              {responding.description && (
                <Typography variant="body2" color="text.secondary" mb={2}>{responding.description}</Typography>
              )}
              <Stack spacing={3} mt={1}>
                {responding.questions.map((q) => (
                  <Box key={q.id}>
                    <Typography fontSize={14} fontWeight={600} mb={1}>{q.label}</Typography>
                    <QuestionInput q={q} value={answers[q.id] ?? null} onChange={(v) => setAns(q.id, v)} />
                  </Box>
                ))}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setResponding(null)} sx={{ color: 'text.secondary' }}>Ahora no</Button>
              <Button variant="contained" onClick={handleSubmit} disabled={isLoading}
                sx={{ fontWeight: 700 }}>
                Enviar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast?.severity ?? 'success'} variant="filled" onClose={() => setToast(null)} sx={{ fontWeight: 600 }}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

/**
 * La encuesta como tarjeta flotante, con las preguntas a la vista: son tres y se contestan
 * sin abrir nada. Va abajo a la derecha, ARRIBA de la burbuja de Gustavo, para no taparla.
 * "Ahora no" la esconde hasta la próxima vez que entre. (12/10/2026)
 */
function EncuestaFlotante({ survey, answers, onAnswer, onSubmit, onClose, enviando }: {
  survey: Survey;
  answers: Record<string, number | string | null>;
  onAnswer: (qid: string, v: number | string | null) => void;
  onSubmit: () => void;
  onClose: () => void;
  enviando: boolean;
}) {
  const [abierta, setAbierta] = useState(true);
  // Las de opción son obligatorias; el texto libre no.
  const completa = survey.questions.every((q) => q.type === 'text' || answers[q.id] != null);

  return (
    <Box role="dialog" aria-label={survey.title} sx={{
      position: 'fixed', zIndex: 1250,
      right: { xs: 12, md: 24 }, bottom: { xs: 84, md: 96 },
      width: { xs: 'calc(100vw - 24px)', sm: 400 },
      maxHeight: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column',
      borderRadius: 3, overflow: 'hidden',
      bgcolor: 'background.paper',
      border: `1px solid ${alpha(golden, 0.35)}`,
      boxShadow: '0 18px 48px rgba(0,0,0,0.45)',
      '@keyframes encuestaEntra': { from: { opacity: 0, transform: 'translateY(12px)' }, to: { opacity: 1, transform: 'none' } },
      animation: 'encuestaEntra 220ms ease-out',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5,
        bgcolor: alpha(golden, 0.08), borderBottom: abierta ? `1px solid ${P.border.subtle}` : 'none',
      }}>
        <PollOutlinedIcon sx={{ color: golden, fontSize: 20 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontSize={14} fontWeight={700} lineHeight={1.3}>{survey.title}</Typography>
          {survey.description && (
            <Typography fontSize={12} color="text.secondary" lineHeight={1.3}>{survey.description}</Typography>
          )}
        </Box>
        <IconButton size="small" aria-label={abierta ? 'Minimizar' : 'Abrir'} onClick={() => setAbierta((a) => !a)}
          sx={{ color: 'text.secondary' }}>
          {abierta ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
        </IconButton>
        <IconButton size="small" aria-label="Cerrar" onClick={onClose} sx={{ color: 'text.disabled' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {abierta && (
        <>
          <Stack spacing={2.25} sx={{ px: 2, py: 2, overflowY: 'auto' }}>
            {survey.questions.map((q, i) => (
              <Box key={q.id}>
                <Typography fontSize={13.5} fontWeight={600} mb={1} lineHeight={1.4}>
                  <Box component="span" sx={{ color: golden, mr: 0.75 }}>{i + 1}.</Box>{q.label}
                </Typography>
                <QuestionInput q={q} value={answers[q.id] ?? null} onChange={(v) => onAnswer(q.id, v)} compacta />
              </Box>
            ))}
          </Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ px: 2, pb: 2 }}>
            <Button size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>Ahora no</Button>
            <Button size="small" variant="contained" onClick={onSubmit} disabled={!completa || enviando}
              sx={{ fontWeight: 700 }}>
              Enviar
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
}

export function QuestionInput({ q, value, onChange, compacta }: {
  q: SurveyQuestion;
  value: number | string | null;
  onChange: (v: number | string | null) => void;
  /** Escala más chica, para que 1–10 entre en una tarjeta angosta. */
  compacta?: boolean;
}) {
  if (q.type === 'text') {
    return (
      <TextField fullWidth multiline minRows={2} size="small"
        placeholder="Escribí acá…" value={(value as string) ?? ''}
        onChange={(e) => onChange(e.target.value)} />
    );
  }
  /**
   * Sí/No: dos botones, no una escala de 1. Se guarda 1 = sí y 0 = no, así el promedio
   * de la pregunta da directamente el % de confirmaciones sin lógica aparte.
   * (pedido de Admin Harmony para confirmar asistencia a reuniones, 01/08/2026)
   */
  if (q.type === 'yesno') {
    const OPCIONES: Array<{ v: number; label: string }> = [
      { v: 1, label: q.labels?.[0] ?? 'Sí' }, { v: 0, label: q.labels?.[1] ?? 'No' },
    ];
    return (
      <Stack direction="row" spacing={1}>
        {OPCIONES.map((o) => {
          const selected = value === o.v;
          return (
            <Box key={o.v} onClick={() => onChange(o.v)}
              sx={{
                px: 3, height: 38, borderRadius: 2, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13.5, fontWeight: 700, userSelect: 'none',
                border: `1px solid ${selected ? golden : P.border.subtle}`,
                bgcolor: selected ? golden : 'transparent',
                color: selected ? '#1A1A1A' : 'text.primary',
                transition: 'all 0.12s',
                '&:hover': { borderColor: golden },
              }}>
              {o.label}
            </Box>
          );
        })}
      </Stack>
    );
  }

  const n = q.type === 'nps' ? 11 : (q.max ?? 5);          // nps: 0..10 ; scale: 1..max
  const start = q.type === 'nps' ? 0 : 1;
  const options = Array.from({ length: n }, (_, i) => start + i);
  return (
    <Stack direction="row" spacing={compacta ? 0.5 : 0.75} flexWrap="wrap" useFlexGap>
      {options.map((opt) => {
        const selected = value === opt;
        return (
          <Box key={opt} onClick={() => onChange(opt)}
            sx={{
              width: compacta ? 30 : 38, height: compacta ? 30 : 38, borderRadius: '50%', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, userSelect: 'none',
              border: `1px solid ${selected ? golden : P.border.subtle}`,
              bgcolor: selected ? golden : 'transparent',
              color: selected ? '#1A1A1A' : 'text.primary',
              transition: 'all 0.12s',
              '&:hover': { borderColor: golden },
            }}>
            {opt}
          </Box>
        );
      })}
    </Stack>
  );
}
